import EventSequence from '../../../../@types/event-sequence';
import {
    ModelLintRequest,
    ModelLintSnapshot,
    ShakedownRequest,
    ShakedownSnapshot,
} from '../../../../@types/model-quality';
import {TestMappingRequest, TestMappingSnapshot} from '../../../../@types/test-mapping';
import EventSequenceService from '../../../../services/event-sequence.service';
import componentService from '../../../../services/component.service';

/**
 * What the Model Testing tools are pointed at. A Safety View and a Component are both a named thing
 * with a fault tree, and the lint and the test mapping both traverse that tree, so the tools differ
 * only in which endpoint answers and which cache entry holds the result.
 */
export type TestingScopeKind = 'eventSequence' | 'component';

export interface TestingScope {
    kind: TestingScopeKind;
    id: number;
    /** What it is called, for panels that label a result with it. Not part of its identity. */
    name?: string;
}

/**
 * The cache key for a scope. Ids are only unique within a kind, so a bare number would let a
 * component and a safety view that happen to share an id show each other's results.
 */
export function scopeKey(scope: TestingScope): string {
    return `${scope.kind}:${scope.id}`;
}

/** Starts a lint run against whichever kind of thing the scope names. */
export function startLintForScope(scope: TestingScope, request: ModelLintRequest): Promise<{runId: string}> {
    return scope.kind === 'component'
        ? componentService.startModelLint(scope.id, request)
        : EventSequenceService.startModelLint(scope.id, request);
}

/** Polls the snapshot of a lint run started for this scope. */
export function lintSnapshotForScope(scope: TestingScope, runId: string): Promise<ModelLintSnapshot> {
    return scope.kind === 'component'
        ? componentService.getModelLintSnapshot(scope.id, runId)
        : EventSequenceService.getModelLintSnapshot(scope.id, runId);
}

/** Starts a single-report test mapping against whichever kind of thing the scope names. */
export function startTestMappingForScope(
    scope: TestingScope,
    request: TestMappingRequest,
): Promise<{runId: string}> {
    return scope.kind === 'component'
        ? componentService.startTestMapping(scope.id, request)
        : EventSequenceService.startTestMapping(scope.id, request);
}

/** Polls the snapshot of a test mapping run started for this scope. */
export function testMappingSnapshotForScope(
    scope: TestingScope,
    runId: string,
): Promise<TestMappingSnapshot> {
    return scope.kind === 'component'
        ? componentService.getTestMappingSnapshot(scope.id, runId)
        : EventSequenceService.getTestMappingSnapshot(scope.id, runId);
}

/**
 * The model the result view draws the mapping onto.
 *
 * The dendrogram reads its tree from `events[0].faultTree`, which is where an event sequence keeps
 * the initiating event's tree. A Component's tree is its own interior with no events around it, so
 * it is presented as the one event the view looks for rather than teaching the view a second shape.
 */
export async function modelForScope(scope: TestingScope): Promise<EventSequence> {
    if (scope.kind !== 'component') {
        return EventSequenceService.getEventSequenceById(scope.id);
    }
    const component = await componentService.getComponent(scope.id);
    return {
        id: component.id,
        uniqueId: component.uniqueId,
        name: component.name,
        description: component.description,
        events: [{
            id: component.faultTreeId ?? component.id,
            uniqueId: component.uniqueId,
            name: component.name,
            faultTree: component.faultTree,
        }],
    } as unknown as EventSequence;
}

/** Starts a shakedown against whichever kind of thing the scope names. */
export function startShakedownForScope(
    scope: TestingScope,
    request: ShakedownRequest,
): Promise<{runId: string}> {
    return scope.kind === 'component'
        ? componentService.startShakedown(scope.id, request)
        : EventSequenceService.startShakedown(scope.id, request);
}

/** Polls the snapshot of a shakedown run started for this scope. */
export function shakedownSnapshotForScope(
    scope: TestingScope,
    runId: string,
): Promise<ShakedownSnapshot> {
    return scope.kind === 'component'
        ? componentService.getShakedownSnapshot(scope.id, runId)
        : EventSequenceService.getShakedownSnapshot(scope.id, runId);
}

/**
 * The working area owning this scope, which scopes the report sources a shakedown can draw from.
 * Undefined when it has none, which the source list treats as unscoped.
 */
export async function groupIdForScope(scope: TestingScope): Promise<number | undefined> {
    if (scope.kind === 'component') {
        return (await componentService.getComponent(scope.id)).groupId;
    }
    const eventSequence = await EventSequenceService.getEventSequenceById(scope.id) as {group?: {id: number}};
    return eventSequence?.group?.id;
}
